import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import UserAvatarSVG from '../../../components/UserAvatarSVG'
import MediaAndPromotionsData from '../ExecutiveDash/MediaAndPromotionsData';
import '../../../styles/MarketingDashStyles.scss'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import HexagonLoader from '../../../components/HexagonLoader/HexagonLoader';
import * as Sentry from '@sentry/react';

const BudgetBarShape = (props) => {
    const { x, y, width, height } = props;
    return (
        <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="none"
            stroke="#333"
            strokeWidth={2}
            rx={2}
            ry={2}
        />
    );
};

const renderLegend = (props) => {
    const { payload } = props;
    return (
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', paddingBottom: '8px' }}>
            {payload.map((entry, index) => {
                const isBudget = entry.dataKey === 'budget';
                return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                        {isBudget ? (
                            <svg width="14" height="14">
                                <rect
                                    x={1} y={1} width={12} height={12}
                                    fill="white"
                                    stroke="#333"
                                    strokeWidth={2}
                                    rx={2}
                                />
                            </svg>
                        ) : (
                            <svg width="14" height="14">
                                <rect x={1} y={1} width={12} height={12} fill="#f44336" rx={2} />
                            </svg>
                        )}
                        <span>{entry.value}</span>
                    </div>
                );
            })}
        </div>
    );
};

const IncomeBarChart = ({ data, title, actualLabel, formatPeriod }) => (
    <div className="income-bar-chart">
        <div className="income-bar-chart__heading">
            {title}
        </div>
        <ResponsiveContainer width="100%" height={260}>
            <BarChart
                data={data.map(d => ({ ...d, periodLabel: formatPeriod(d.period) })).reverse()}
                barGap={-20}
                margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
            >
                <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                <XAxis dataKey="periodLabel" />
                <YAxis tickFormatter={v => v.toLocaleString()} />
                <Tooltip formatter={v => v.toLocaleString()} />
                <Legend content={renderLegend} />
                <Bar
                    dataKey="actual"
                    name={actualLabel}
                    fill="#f44336"
                    barSize={20}
                    radius={[2, 2, 0, 0]}
                    legendType="rect"
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="budget"
                    name="Budget"
                    shape={<BudgetBarShape />}
                    barSize={20}
                    legendType="rect"
                    isAnimationActive={false}
                />
            </BarChart>
        </ResponsiveContainer>
    </div>
);

const MarketingDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [period, setPeriod] = useState(null);
    const [mediaData, setMediaData] = useState([]);
    const [promotionsData, setPromotionsData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`);
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                if (e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
    }, [apiFetch]);

    useEffect(() => {
        const timer = setTimeout(() => setInitialLoading(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!period) return;
        let isCancelled = false;

        const fetchMediaData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/MediaActualVsBudget?Period=${period}`);
                const data = await response.json();
                if (!isCancelled && data.success && data.data) {
                    setMediaData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (!isCancelled) console.error('Error fetching media data:', e);
            }
        };

        const fetchPromotionsData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/PromotionsActualVsBudget?Period=${period}`);
                const data = await response.json();
                if (!isCancelled && data.success && data.data) {
                    setPromotionsData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (!isCancelled) console.error(e);
            }
        };

        fetchMediaData();
        fetchPromotionsData();

        return () => { isCancelled = true; };
    }, [apiFetch, period]);

    const formatPeriod = (period) => {
        if (!period) return '';
        const year = Math.floor(period / 100);
        const month = (period % 100) - 1;
        const date = new Date(year, month, 1);
        return date.toLocaleString('default', { month: 'short', year: '2-digit' });
    };

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div className="marketing-dashboard">
                    <div>
                        <h1 className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user.FirstName || user.firstName}
                        </h1>
                        <h3>Media and Promotions Dashboard</h3>
                    </div>
                    <div className="grid-2x2">
                        <div className="table-cell">
                            <MediaAndPromotionsData period={period} />
                        </div>
                        <div className="empty-cell"></div>
                        <div className="chart-cell-1">
                            <IncomeBarChart
                                data={promotionsData}
                                title="Promotions Income"
                                actualLabel="Promotions Income"
                                formatPeriod={formatPeriod}
                            />
                        </div>
                        <div className="chart-cell-2">
                            <IncomeBarChart
                                data={mediaData}
                                title="Media Income"
                                actualLabel="Media Income"
                                formatPeriod={formatPeriod}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MarketingDashboard;